import React, { useMemo } from 'react';
import {
  InfiniteTable,
  InfiniteTableProps,
  SUPPLIER_CONTACT_TAGS,
} from 'components';
import { formatPhoneNumber, formatPhoneNumberAsLink } from 'utils';
import { ContactListSortOrder } from '@bb/contacts';

import {
  LinkCell,
  TagsCell,
  NameCell,
  TextCell,
  AddressListingCell,
} from './TableCells';

export type SupplierContactTableProps = Omit<InfiniteTableProps, 'columns'> & {
  sort?: {
    field: string;
    order: ContactListSortOrder;
  };
  filter?: Record<string, string[]>;
};

const SupplierContactTable: React.FC<SupplierContactTableProps> = ({
  sort,
  filter,
  ...props
}) => {
  const SUPPLIER_COLUMNS = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'firstName',
        width: 220,
        sortOrder: sort?.field === 'firstName' && sort.order,
        sorter: true,
        render: (text?: string, record?: any) => (
          <NameCell
            loading={!record}
            firstName={record?.firstName}
            lastName={record?.lastName}
          />
        ),
      },
      {
        title: 'Company',
        dataIndex: 'companyName',
        width: 150,
        sortOrder: sort?.field === 'companyName' && sort.order,
        sorter: true,
        render: (text?: string, record?: any) => (
          <TextCell loading={!record} value={record?.companyName} />
        ),
      },
      {
        title: 'type',
        dataIndex: 'tags',
        width: 150,
        sortOrder: sort?.field === 'tags' && sort.order,
        sorter: true,
        filteredValue: filter?.tags || null,
        filters: Object.values(SUPPLIER_CONTACT_TAGS).map(
          ({ label, value }) => ({
            text: label,
            value,
          }),
        ),
        render: (text?: string, record?: any) => (
          <TagsCell loading={!record} tags={record?.tags} />
        ),
      },
      {
        title: 'Work',
        dataIndex: 'phones',
        width: 200,
        render: (text?: string, record?: any) => (
          <LinkCell
            loading={!record}
            value={formatPhoneNumber(
              record?.phones?.find(({ type }: any) => type === 'work')
                ?.number || '',
            )}
            linktext={formatPhoneNumberAsLink(
              record?.phones?.find(({ type }: any) => type === 'work')
                ?.number || '',
            )}
          />
        ),
      },
      {
        title: 'Mobile',
        dataIndex: 'phones',
        width: 200,
        render: (text?: string, record?: any) => (
          <LinkCell
            loading={!record}
            value={formatPhoneNumber(
              record?.phones?.find(({ type }: any) => type === 'mobile')
                ?.number || '',
            )}
            linktext={formatPhoneNumberAsLink(
              record?.phones?.find(({ type }: any) => type === 'mobile')
                ?.number || '',
            )}
          />
        ),
      },
      {
        title: 'email',
        dataIndex: 'email',
        width: 170,
        render: (text?: string, record?: any) => (
          <LinkCell loading={!record} value={record?.email} type="email" />
        ),
      },
      {
        title: 'Address',
        dataIndex: 'companyAddress',
        width: 380,
        render: (text?: string, record?: any) => (
          <AddressListingCell
            loading={!record}
            address={record?.companyAddress}
          />
        ),
      },
    ],
    [sort, filter],
  );
  return <InfiniteTable {...props} columns={SUPPLIER_COLUMNS} />;
};

export default SupplierContactTable;
